import { Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { LUNCH_CUTOFF_SECONDS } from '../../types/lunch.types';

export interface MealClockProps {
  /** The target lunch day in YYYY-MM-DD */
  targetDate: string;
  className?: string;
}

/** Formats duration into e.g. "2 h 15 min" or "45 min". */
const formatTimeRemaining = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours} h${mins > 0 ? ` ${mins} min` : ''}`;
  }
  return `${Math.max(1, minutes)} min`;
};

/**
 * Calculates remaining minutes until 11:00 AM on targetDate in organization time zone.
 */
const getMinutesRemaining = (targetDate: string): number => {
  const timeZone = getOrganizationTimeZone();
  const now = new Date();
  const {
    year: curYear,
    month: curMonth,
    day: curDay,
    hour: curHour,
    minute: curMinute,
    second: curSecond,
  } = orgWallClock(now, timeZone);

  const [tYear, tMonth, tDay] = targetDate.split('-').map(Number);
  if (!tYear || !tMonth || !tDay) return 0;

  // Current time in seconds of current day
  const curSeconds = curHour * 3600 + curMinute * 60 + curSecond;

  // Difference in days
  const curUtcMidnight = Date.UTC(curYear, curMonth - 1, curDay);
  const targetUtcMidnight = Date.UTC(tYear, tMonth - 1, tDay);
  const diffDays = Math.round((targetUtcMidnight - curUtcMidnight) / 86400000);

  // Target cutoff time in seconds relative to current day
  const cutoffSeconds = diffDays * 86400 + LUNCH_CUTOFF_SECONDS;
  const remainingSeconds = cutoffSeconds - curSeconds;

  return Math.max(0, Math.round(remainingSeconds / 60));
};

/**
 * Inline countdown timer inside the Meal Hero banner.
 * Matches HR Module Prototype `.meal-clock`.
 */
export const MealClock = ({ targetDate, className = '' }: MealClockProps) => {
  const { t } = useTranslation();
  const [leftMinutes, setLeftMinutes] = useState(() => getMinutesRemaining(targetDate));

  useEffect(() => {
    setLeftMinutes(getMinutesRemaining(targetDate));
    const interval = setInterval(() => {
      setLeftMinutes(getMinutesRemaining(targetDate));
    }, 30000); // refresh every 30 seconds
    return () => clearInterval(interval);
  }, [targetDate]);

  const isSoon = leftMinutes <= 60 && leftMinutes > 0;
  // Percentage of the 24h ordering window elapsed (min 4%, max 100%)
  const progressPercent = Math.max(4, Math.min(100, (1 - leftMinutes / 1440) * 100));

  // Format closing label: "11:00 AM on [Date]"
  const [year, month, day] = targetDate.split('-').map(Number);
  const targetObj = new Date(year, month - 1, day);
  const formattedDay = targetObj.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      role="group"
      aria-label="Time left to order"
      className={`relative flex flex-col justify-center gap-2.5 py-4 pl-0 pr-4 sm:py-6 sm:pr-6 md:py-7 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <Timer
          className={`size-6 shrink-0 transition-colors ${
            isSoon ? 'text-warning-600 dark:text-warning-400' : 'text-primary'
          }`}
        />
        <div className="flex flex-col">
          <b
            className={`text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl ${
              isSoon ? 'text-warning-700 dark:text-warning-300' : 'text-foreground'
            }`}
          >
            {formatTimeRemaining(leftMinutes)}
          </b>
          <span className="text-xs text-muted-foreground sm:text-sm">
            {t('LEFT_TO_ORDER_OR_CANCEL', { defaultValue: 'left to order or cancel' })}
          </span>
        </div>
      </div>

      {/* Progress track */}
      <div
        role="progressbar"
        aria-label="Ordering window"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progressPercent)}
        className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-200/60 dark:bg-neutral-800"
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isSoon ? 'bg-warning-500' : 'bg-primary'
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <span className="text-xs text-muted-foreground">
        {t('CLOSES_AT', { defaultValue: 'Closes at' })} 11:00 AM {formattedDay}
      </span>
    </div>
  );
};
